import { Product } from "../../api/entities/Product";
import React, { useEffect, useState } from "react";
import { ProductEditor } from "./ProductEditor";
import {
  EuiBasicTableColumn,
  EuiFlexGroup,
  EuiFlexItem,
  EuiInMemoryTable,
  EuiHealth,
  EuiPanel,
  EuiTitle,
} from "@elastic/eui";
import {
  useGetProductGroupsQuery,
  useUpdateProductGroupMutation,
  useUpdateProductMutation,
} from "../../api/rtkQueryApi/ProductsApi";
import { ProductGroup } from "../../api/entities/ProductGroup";
import { ProductGroupEditor } from "./ProductGroupEditor";
import { JSONClone } from "../../utils/CopyUtils";

export const ProductManagement: React.FC = () => {
  const [editingProduct, setEditingProduct] = useState<Product>();
  const [editingProductGroup, setEditingProductGroup] =
    useState<ProductGroup>();
  const [updatedProduct, updateProductQuery] = useUpdateProductMutation();
  const [updatedProductGroup, updateProductGroupQuery] =
    useUpdateProductGroupMutation();

  const productsGroupsQuery = useGetProductGroupsQuery();
  const [selectedProductGroup, setSelectedProductGroup] =
    useState<ProductGroup>();

  useEffect(() => {
    if (productsGroupsQuery.data) {
      if (selectedProductGroup) {
        setSelectedProductGroup(
          productsGroupsQuery.data.find(
            (group) => group.id === selectedProductGroup.id,
          ),
        );
      }
    }
  }, [productsGroupsQuery.data]);

  const onProductSelected = (product: Product) => {
    setEditingProduct(JSONClone(product));
    setEditingProductGroup(undefined);
  };

  const onProductSaveClick = () => {
    if (editingProduct) {
      updatedProduct({ product: editingProduct });
    }
  };

  const onProductGroupSaveClick = () => {
    if (editingProductGroup) {
      updatedProductGroup({ productGroup: editingProductGroup });
    }
  };

  const onProductGroupExpandClicked = (productGroup: ProductGroup) => {
    setSelectedProductGroup(productGroup);
    setEditingProductGroup(undefined);
    setEditingProduct(undefined);
  };

  const onProductGroupEditClicked = (productGroup: ProductGroup) => {
    setEditingProductGroup(JSONClone(productGroup));
    setSelectedProductGroup(undefined);
    setEditingProduct(undefined);
  };

  const productGroupColumns: EuiBasicTableColumn<ProductGroup>[] = [
    {
      field: "",
      width: "40px",
      render: (group: ProductGroup) =>
        group.id === selectedProductGroup?.id ||
        group.id === editingProductGroup?.id ? (
          <EuiHealth color="primary" />
        ) : (
          ""
        ),
    },
    {
      field: "name",
      name: "Name",
    },
    {
      field: "pricePerMonth",
      name: "Price Per Month",
      render: (price: number) => `$${price}`,
    },
    {
      field: "",
      name: "Actions",
      actions: [
        {
          name: "Edit",
          description: "Edit",
          type: "icon",
          icon: "pencil",
          onClick: (group: ProductGroup) => onProductGroupEditClicked(group),
        },
        {
          name: "View",
          description: "View Products",
          type: "icon",
          icon: "arrowRight",
          onClick: (group: ProductGroup) => onProductGroupExpandClicked(group),
        },
      ],
    },
  ];

  const productColumns: EuiBasicTableColumn<Product>[] = [
    {
      field: "",
      width: "40px",
      render: (product: Product) =>
        product.id === editingProduct?.id ? <EuiHealth color="primary" /> : "",
    },
    {
      field: "name",
      name: "Name",
    },
    {
      field: "pricePerMonth",
      name: "Overridden Price Per Month",
      render: (price: number) =>
        price !== undefined && price !== null ? `$${price}` : "",
    },
    {
      field: "",
      name: "Actions",
      actions: [
        {
          name: "Edit",
          description: "Edit",
          type: "icon",
          icon: "pencil",
          onClick: (product: Product) => onProductSelected(product),
        },
      ],
    },
  ];

  return (
    <EuiFlexGroup>
      <EuiFlexItem>
        <EuiPanel hasShadow={false} hasBorder={true}>
          <EuiTitle size={"xxs"}>
            <h2>Product Groups</h2>
          </EuiTitle>
          <EuiInMemoryTable
            items={productsGroupsQuery.data ?? []}
            itemId="productGroup"
            columns={productGroupColumns}
          />
        </EuiPanel>
      </EuiFlexItem>
      {editingProductGroup && (
        <EuiFlexItem>
          <ProductGroupEditor
            productGroup={editingProductGroup}
            onProductGroupUpdated={setEditingProductGroup}
            onSaveClick={onProductGroupSaveClick}
            isLoading={updateProductGroupQuery.isLoading}
          />
        </EuiFlexItem>
      )}
      <EuiFlexItem>
        {selectedProductGroup && (
          <EuiPanel hasShadow={false} hasBorder={true}>
            <EuiTitle size={"xxs"}>
              <h2>Products</h2>
            </EuiTitle>
            <EuiInMemoryTable
              items={selectedProductGroup.products}
              itemId="products"
              columns={productColumns}
            />
          </EuiPanel>
        )}
      </EuiFlexItem>
      {selectedProductGroup && (
        <EuiFlexItem>
          {editingProduct && (
            <ProductEditor
              product={editingProduct}
              onUpdateProduct={setEditingProduct}
              onSaveClick={onProductSaveClick}
              isLoading={updateProductQuery.isLoading}
            />
          )}
        </EuiFlexItem>
      )}
      {!selectedProductGroup && !editingProductGroup && <EuiFlexItem />}
    </EuiFlexGroup>
  );
};
